import { JSX, MouseEvent, useCallback, useMemo } from 'react'

import { useAccount } from 'wagmi'

import { useApproveTransactionAction } from '~/components/smart-accounts/useApproveTransactionAction'
import { PrivyWalletBridge } from '~/components/smart-accounts/usePrivyWalletBridge'
import { useRecoverySignerModal } from '~/components/smart-accounts/useRecoverySignerModal'
import { useApproveAndSubmitTransaction } from '~/hooks/smart-account-actions/useApproveAndSubmitTransaction'
import { useConnectedAddress } from '~/hooks/useConnectedAddress'
import { useTransactionSmartAccount } from '~/hooks/useSmartAccounts'
import { BackendTransaction, SmartAccountRole } from '~/types'
import { Address } from '~/utils/address'
import { emailsMatch } from '~/utils/email'
import { captureException } from '~/utils/errors'
import { isSmartAccountOrg } from '~/utils/smartAccounts'
import { connectedAddressIsEoaSigner } from '~/utils/smartAccounts/signingReadiness'

type ApproveEmailSignerMode = 'email-signer' | 'connect' | 'default'

type ApproveEmailSignerResult = {
  mode: ApproveEmailSignerMode
  isReady: boolean
  isWaitingForWallet: boolean
  onApprove: (e: MouseEvent<HTMLButtonElement>) => void
  triggerShortcut: () => void
  buttonText: string
  disabledReason: string | JSX.Element | undefined
  isLoading: boolean
  isDisabled: boolean
  hasPrivyUser: boolean
  showDisconnectButton: boolean
  showPrivyDisconnect: boolean
  disconnectPrivyAndEOAs: () => Promise<void>
  actionEventName: string | undefined
  actionEventProperties: { transactionId: string } | undefined
  isApprovalActionLoading: boolean
}

/**
 * Centralizes the decision tree that governs the approve/connect/default
 * behavior of `ApproveTransactionButton`. Derives a single `mode` plus all the
 * render data the button needs, so the component stays a thin renderer.
 *
 * `mode`:
 * - `email-signer`: the Privy-authenticated user matches a stored email
 *   signer — clicking runs the direct approval flow.
 * - `connect`: no wallet connected, Privy user absent, but recovery signers
 *   exist — clicking opens the recovery-signer modal.
 * - `default`: falls through to the underlying `useApproveTransactionAction`
 *   (sign / submit / execute / connect-modal paths).
 */
export const useApproveEmailSigner = ({
  transaction,
  onClose,
  hasAccessOverride,
  onConfirmation,
  onSubmitError,
  isDisabledOverride,
  disabledReasonOverride,
  privyBridge,
}: {
  transaction: BackendTransaction
  onClose?: () => void
  hasAccessOverride?: boolean
  onConfirmation?: () => void
  onSubmitError?: (e: unknown) => void
  isDisabledOverride?: boolean
  disabledReasonOverride?: string | JSX.Element
  privyBridge: PrivyWalletBridge
}): ApproveEmailSignerResult => {
  const { approveAndSubmitTransaction, isProcessing } =
    useApproveAndSubmitTransaction()
  const { smartAccount } = useTransactionSmartAccount(transaction)
  const { isConnected } = useAccount()
  const connectedAddress = useConnectedAddress()

  const isRootSmartAccount =
    isSmartAccountOrg(smartAccount) &&
    smartAccount?.role === SmartAccountRole.ROOT

  const {
    hasPrivyUser,
    privyEmail,
    isActivatingWallet,
    disconnectPrivyAndEOAs,
  } = privyBridge

  // Auto-disconnect runs after any successful sign: the signer's work is done
  // (submit is a network-level op that doesn't need the signing wallet), and
  // leaving the session active shows a stale "Disconnect" affordance on the
  // dialog. handleSign fires only on approveTransaction's onSuccess, so
  // user-cancelled Privy prompts (which reject sign) do not reach here.
  //
  // `disconnectPrivyAndEOAs` covers both user types: Privy email users get
  // privyLogout() + wagmi disconnect(); external EOA users get only the wagmi
  // half (the `authenticated ? privyLogout() : Promise.resolve()` branch
  // inside the bridge). See docs/plans/2026-04-17-docs-privy-logout-vs-eoa-
  // disconnect-plan.md §8.1, §8.2.
  //
  // Tradeoff: if submit fails after a successful sign on threshold=1 or
  // multi-sig-final flows, the user must re-authenticate to retry. Accepted —
  // submit retry doesn't need the signing wallet, and the alternative
  // (leaving the session alive) strands the dialog with a stale wagmi
  // connector, which is the bug this resolved on 2026-04-17.
  const handleSign = useCallback(() => {
    void disconnectPrivyAndEOAs().catch((e) => captureException(e))
  }, [disconnectPrivyAndEOAs])

  const handleConfirmation = useCallback(() => {
    void disconnectPrivyAndEOAs().catch((e) => captureException(e))
    onConfirmation?.()
  }, [disconnectPrivyAndEOAs, onConfirmation])

  const actionConfig = useApproveTransactionAction({
    transaction,
    smartAccount,
    hasAccessOverride,
    isProcessing,
    approveAndSubmitTransaction,
    onClose,
    onSign: handleSign,
    onConfirmation: handleConfirmation,
    onSubmitError,
  })

  const eoaSigners = useMemo(
    () => smartAccount?.eoaSigners ?? [],
    [smartAccount?.eoaSigners],
  )
  const hasEmailSigners = eoaSigners.some((s) => !!s.email)

  const { recoverySignerItems, openRecoverySignerModal } =
    useRecoverySignerModal(eoaSigners)

  // The Privy-authenticated user's email matches a stored email signer.
  // This gates the email-signer UI path (buttons, text), not signing readiness.
  const hasMatchingEmailSigner =
    hasPrivyUser &&
    hasEmailSigners &&
    !!privyEmail &&
    eoaSigners.some((s) => emailsMatch(s.email, privyEmail))

  // True once wagmi has actually reflected the Privy embedded wallet — i.e.
  // useAccount().address equals a stored email signer's address. When this is
  // false but `hasMatchingEmailSigner` is true, the user IS authorized; we're
  // just waiting for wagmi's React state to propagate after setActiveWallet.
  // Without this gate, a click in that window would fall through the
  // useEOASigning branch in useApproveAndSubmitTransaction and surface as a
  // misleading passkey error.
  //
  // `connectedAddressIsEoaSigner` is the same predicate consumed by
  // `checkEoaSigningReadiness` inside `useApproveAndSubmitTransaction`, so the
  // button's enablement and the hook's guard share one source of truth.
  const isEmailSignerWalletReady =
    hasMatchingEmailSigner &&
    connectedAddressIsEoaSigner({
      connectedAddress,
      smartAccount: { eoaSigners },
    })

  // The recovery-signer modal is a ROOT-account-only affordance: passkey
  // signing is blocked server-side on ROOT (see
  // splits/src/api/internal/accounts/transactions/index.ts:596), so EOA
  // signers are the only signing path. On every other role, EOA signers are
  // regular first-class signers and must not preempt the passkey flow — fall
  // through to `default` instead so useApproveTransactionAction can pick
  // between passkey and EOA based on what the user actually has.
  const shouldShowSignerModal =
    isRootSmartAccount &&
    !isConnected &&
    !hasPrivyUser &&
    recoverySignerItems.length > 0

  // True when wagmi is connected to the address of one of the account's email
  // signers — meaning the connection came from a Privy login, even if Privy
  // has since logged out but wagmi hasn't yet reflected the disconnect.
  // Used to route the Disconnect button through `disconnectPrivyAndEOAs`
  // (Privy logout + wagmi disconnect) instead of wagmi-only disconnect, so
  // the user isn't stranded on the dialog with a still-resident Privy session
  // after the wagmi-only path runs.
  const isConnectedToEmailSigner =
    isConnected &&
    !!connectedAddress &&
    eoaSigners.some(
      (s) => !!s.email && Address.equal(s.address, connectedAddress),
    )

  // Disconnect routed through the Privy-aware teardown.
  const showPrivyDisconnect =
    (hasPrivyUser || (isRootSmartAccount && isConnectedToEmailSigner)) &&
    !isProcessing

  // Disconnect routed through wagmi-only — for plain EOA wallets with no
  // Privy session attached.
  const showDisconnectButton =
    isConnected &&
    isRootSmartAccount &&
    !hasPrivyUser &&
    !isConnectedToEmailSigner

  const mode: ApproveEmailSignerMode = hasMatchingEmailSigner
    ? 'email-signer'
    : shouldShowSignerModal
      ? 'connect'
      : 'default'

  const triggerApproval = actionConfig.triggerApproval

  const onApprove = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (mode === 'email-signer') {
        e.stopPropagation()
        triggerApproval()
        return
      }
      if (mode === 'connect') {
        e.stopPropagation()
        openRecoverySignerModal()
        return
      }
      actionConfig.onClick(e)
    },
    [mode, triggerApproval, openRecoverySignerModal, actionConfig],
  )

  const triggerShortcut = useCallback(() => {
    if (mode === 'connect') {
      openRecoverySignerModal()
      return
    }
    triggerApproval()
  }, [mode, openRecoverySignerModal, triggerApproval])

  const buttonText = mode === 'connect' ? 'Connect' : actionConfig.text

  const isWaitingForWallet =
    mode === 'email-signer' && !isEmailSignerWalletReady

  const isReady = mode === 'email-signer' ? isEmailSignerWalletReady : true

  const isLoading =
    mode === 'email-signer'
      ? isActivatingWallet || !!actionConfig.isLoading
      : !!actionConfig.isLoading

  const isDisabled =
    mode === 'email-signer'
      ? !isEmailSignerWalletReady ||
        isActivatingWallet ||
        !!isDisabledOverride ||
        !!actionConfig.isDisabled
      : !!actionConfig.isDisabled || !!isDisabledOverride

  // In email-signer mode, wallet-state messages take precedence over the
  // underlying action's reason (e.g. "Waiting for wallet..." beats the
  // action's "not a signer" message during the brief window before wagmi
  // reflects the Privy embedded wallet). Once the wallet is ready, fall
  // through to actionConfig — that's how "You have already signed this
  // transaction" reaches the dialog for Privy email signers, matching the
  // behavior EOA signers already see.
  const disabledReason: string | JSX.Element | undefined =
    mode === 'email-signer'
      ? isActivatingWallet
        ? 'Activating wallet...'
        : isWaitingForWallet
          ? 'Waiting for wallet...'
          : isDisabledOverride
            ? disabledReasonOverride
            : 'disabledReason' in actionConfig
              ? actionConfig.disabledReason
              : undefined
      : isDisabledOverride
        ? disabledReasonOverride
        : 'disabledReason' in actionConfig
          ? actionConfig.disabledReason
          : undefined

  const actionEventName =
    mode === 'default' && 'eventName' in actionConfig
      ? actionConfig.eventName
      : undefined

  const actionEventProperties =
    mode === 'default' && 'eventProperties' in actionConfig
      ? actionConfig.eventProperties
      : undefined

  return {
    mode,
    isReady,
    isWaitingForWallet,
    onApprove,
    triggerShortcut,
    buttonText,
    disabledReason,
    isLoading,
    isDisabled,
    hasPrivyUser,
    showDisconnectButton,
    showPrivyDisconnect,
    disconnectPrivyAndEOAs,
    actionEventName,
    actionEventProperties,
    isApprovalActionLoading: !!actionConfig.isLoading,
  }
}
